import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { multiFactor, sendEmailVerification, signOut, TotpMultiFactorGenerator, type TotpSecret } from 'firebase/auth';
import { getFirebaseAuth } from '../../lib/firebase';
import { useSession } from '../../lib/useSession';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Banner } from '../../components/Banner';
import { AuthShell } from './AuthShell';
import { StyledQr } from '../../components/StyledQr';
import { authErrorMessage, MFA_ENROLLED_NOTICE } from './mfa';
import { BRAND } from '../../brand';

/** Enrolamiento TOTP obligatorio para Owner, Admin y operador (R1.5). */
export function TotpEnrollPage() {
  const navigate = useNavigate();
  const { user } = useSession();
  const [secret, setSecret] = useState<TotpSecret | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Identity Platform no deja enrolar el segundo factor con el correo sin confirmar. Antes la consola lo
  // decía y no ofrecía salida: había que pedirle el correo a alguien con acceso a la consola de Firebase.
  const [needsEmail, setNeedsEmail] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  async function resendVerification() {
    const current = getFirebaseAuth().currentUser;
    if (!current) return;
    setSending(true);
    setError(null);
    try {
      await sendEmailVerification(current);
      setSent(true);
    } catch (err) {
      setError(authErrorMessage((err as { code?: string }).code));
    } finally {
      setSending(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function start() {
      const current = getFirebaseAuth().currentUser;
      if (!current) return;
      try {
        const session = await multiFactor(current).getSession();
        const generated = await TotpMultiFactorGenerator.generateSecret(session);
        if (!cancelled) setSecret(generated);
      } catch (err) {
        const code = (err as { code?: string }).code;
        if (!cancelled) {
          setNeedsEmail(code === 'auth/unverified-email');
          setError(authErrorMessage(code));
        }
      }
    }
    void start();
    return () => {
      cancelled = true;
    };
  }, [user]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const current = getFirebaseAuth().currentUser;
    if (!secret || !current) return;
    setError(null);
    setLoading(true);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code.replace(/\s/g, ''));
      await multiFactor(current).enroll(assertion, 'App de autenticación');
      // Identity Platform invalida la sesión al agregar el factor y el token vigente no trae
      // `sign_in_second_factor`, que qr-api exige a Owner, Admin y operador (R1.5). Se entra de nuevo con el código.
      await signOut(getFirebaseAuth()).catch(() => undefined);
      navigate('/login', { replace: true, state: { notice: MFA_ENROLLED_NOTICE } });
    } catch (err) {
      setError(authErrorMessage((err as { code?: string }).code));
    } finally {
      setLoading(false);
    }
  }

  const otpauthUrl = secret && user?.email ? secret.generateQrCodeUrl(user.email, BRAND) : null;

  return (
    <AuthShell title="Activa tu segundo factor" subtitle="Tu rol lo requiere. No puedes operar hasta configurarlo.">
      {error && <Banner tone="error">{error}</Banner>}
      {needsEmail && (
        <div className="space-y-3">
          {sent ? (
            <Banner tone="success">
              Te enviamos el correo a {user?.email}. Ábrelo, confirma y vuelve a entrar para activar el segundo factor.
            </Banner>
          ) : (
            <Button type="button" block onClick={resendVerification} loading={sending}>
              Enviarme el correo de confirmación
            </Button>
          )}
        </div>
      )}
      {!secret && !error && <div className="h-24 animate-pulse rounded-md bg-gray-100" aria-label="Generando clave" />}
      {secret && (
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <ol className="list-decimal space-y-3 pl-5 text-sm text-ink">
            <li>
              Escanea este QR con tu app de autenticación (Google Authenticator, 1Password, Authy u otra).
              <div className="mt-3 flex justify-center">
                {otpauthUrl && (
                  <StyledQr value={otpauthUrl} size={256} label={`Código QR para agregar ${BRAND} a tu app de autenticación`} />
                )}
              </div>
              <details className="mt-3 text-sm">
                <summary className="cursor-pointer text-ink-muted">¿No puedes escanear? Ingresa la clave a mano</summary>
                <code className="mt-2 block break-all rounded-sm bg-mist px-2 py-1 font-mono text-xs">{secret.secretKey}</code>
                {otpauthUrl && (
                  <a href={otpauthUrl} className="mt-1 inline-block">
                    Abrir en la app de este celular
                  </a>
                )}
              </details>
            </li>
            <li>Escribe el código de 6 dígitos que muestra la app.</li>
          </ol>
          <Input
            label="Código"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            mono
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="text-center text-xl"
          />
          <Button type="submit" block size="lg" loading={loading} disabled={code.replace(/\s/g, '').length !== 6}>
            Activar segundo factor
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
